/**
 * balance-habitability.ts — Habitability calculator (MFix3.2, PLANETS §2.3).
 *
 * Reads `state.habitability` (loaded from `docs/Habitability.xlsx` via the
 * convert-xlsx pipeline) and exposes three pure helpers:
 *
 *   1. {@link habitabilityFor} — returns a 0..100 score for (planet, race,
 *      researchedTechs) per the formula:
 *
 *        Habitability = BaseInherent(biome)
 *                     + RaceAffinity(race, biome)
 *                     + Σ TechBonus(tech, race, biome) for each researched tech
 *                     + SizeAdjust(planet.size)
 *
 *      Result clamped to [0, 100].
 *
 *   2. {@link habitabilityBandFor} — buckets a score into 'hostile' /
 *      'neutral' / 'hospitable' per the band ranges in the xlsx.
 *
 *   3. {@link popDeltaPerTick} — computes the per-tick population delta:
 *      logistic growth `pop × mul × (1 − pop/popCap)` for non-negative
 *      multipliers (hospitable / neutral) and linear decline `pop × mul`
 *      for the hostile band's negative multiplier. The Cloning Center
 *      economy multiplier from `economyMultipliersForPlanet` stacks on
 *      top in the caller (advance.ts), applying only to non-negative
 *      deltas.
 *
 * Biome label translation: the runtime `BiomeKind` enum has 7 entries
 * (no Toxic / Radioactive in v1) while the xlsx covers 9. {@link BIOME_XLSX_LABEL}
 * maps runtime kinds to the xlsx labels Michael authored. Toxic /
 * Radioactive entries in the xlsx are preserved for future biome additions
 * but never queried in v1 because no planet has those biomes.
 */

import type {
  BiomeKind,
  HabitabilityData,
  Planet,
  PlanetSize,
  RaceId,
} from '../state/types';

/**
 * Runtime BiomeKind → xlsx-label translation. Keep in sync with
 * `BIOME_GRADIENT` in `system-view.ts` (which uses the same conceptual
 * mapping for the rendering palette per GRAPHICS §5.3).
 */
export const BIOME_XLSX_LABEL: Readonly<Record<BiomeKind, string>> = Object.freeze({
  gaia: 'Gaia',
  temperate: 'Tropical',
  desert: 'Desert',
  arctic: 'Frozen',
  ocean: 'Oceanic',
  volcanic: 'Volcanic',
  barren: 'Barren',
});

export type HabitabilityBandName = 'hostile' | 'neutral' | 'hospitable';

/**
 * Per-race per-planet Habitability score, clamped to [0, 100].
 *
 * Reads:
 *  - data.baseInherent[xlsxBiome] — biome baseline
 *  - data.raceAffinity[raceId][xlsxBiome] — racial affinity term
 *  - data.techBonus[*].bonuses[raceId][xlsxBiome] — for each researched tech
 *  - data.sizeModulation[planet.size] — optional size adjustment
 *
 * Missing keys default to 0 (no contribution).
 */
export function habitabilityFor(
  planet: Planet,
  raceId: RaceId,
  researchedTechs: ReadonlySet<string>,
  data: HabitabilityData,
): number {
  const xlsxBiome = BIOME_XLSX_LABEL[planet.biome];
  const baseInherent = data.baseInherent[xlsxBiome] ?? 0;
  const raceAffinity = data.raceAffinity[raceId]?.[xlsxBiome] ?? 0;
  let techBonusTotal = 0;
  for (const techRow of data.techBonus) {
    if (!researchedTechs.has(techRow.techName)) continue;
    techBonusTotal += techRow.bonuses[raceId]?.[xlsxBiome] ?? 0;
  }
  const sizeAdjust = sizeModulationFor(planet.size, data);
  const score = baseInherent + raceAffinity + techBonusTotal + sizeAdjust;
  return Math.max(0, Math.min(100, score));
}

/**
 * Bucket a Habitability score into a band name. Boundaries inclusive at the
 * lower edge of each band per the xlsx Bands sheet (Hostile 0–44, Neutral
 * 45–54, Hospitable 55–100). Scores outside [0, 100] clamp to the nearest
 * band defensively.
 */
export function habitabilityBandFor(
  score: number,
  data: HabitabilityData,
): HabitabilityBandName {
  if (score <= data.bands.hostile.scoreMax) return 'hostile';
  if (score <= data.bands.neutral.scoreMax) return 'neutral';
  return 'hospitable';
}

/**
 * Per-tick population delta from a Habitability score. Logistic for
 * non-negative band multipliers (growth slows as pop approaches popCap);
 * linear for the hostile band's negative multiplier (decline scales
 * with current pop, asymptotic to 0).
 *
 * Caller (advance.ts) applies the Cloning Center economy multiplier on
 * top of any positive delta. Negative deltas are not boosted by Cloning
 * Center — the building grows population, it does not shield decline.
 */
export function popDeltaPerTick(
  score: number,
  currentPop: number,
  popCap: number,
  data: HabitabilityData,
): number {
  const band = habitabilityBandFor(score, data);
  const mul = data.bands[band].perTickMultiplier;
  if (mul < 0) {
    // Linear decline, scales with current pop.
    return currentPop * mul;
  }
  // Logistic growth toward popCap.
  if (currentPop <= 0) return 0;
  if (popCap <= 0) return 0;
  if (currentPop >= popCap) return 0;
  return currentPop * mul * (1 - currentPop / popCap);
}

/**
 * Per-Pop production-efficiency adjustment for the planet's per-tick
 * resource generation. Multiplies the base output by `(1 + adjustment)`,
 * so a Hostile band efficiency of −0.3 yields 0.7× per-Pop output; a
 * Hospitable 0 yields full output. `null` in the xlsx (unspecified)
 * yields 0 (no adjustment) so output is unaffected.
 */
export function productionEfficiencyMultiplier(
  score: number,
  data: HabitabilityData,
): number {
  const band = habitabilityBandFor(score, data);
  const adjust = data.bands[band].productionEfficiency ?? 0;
  return 1 + adjust;
}

/** Size modulation lookup. Xlsx keys are stringified sizes ('5', '6', …, '20'). */
function sizeModulationFor(size: PlanetSize, data: HabitabilityData): number {
  return data.sizeModulation[String(size)] ?? 0;
}
